// Scenario 4: Booked - Location Unknown
// User needs to select both region and area
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { islandScopedDocumentFilter, sanityFetch } from '../../lib/sanity';
import { useRegionsData } from '../../hooks/useRegionsData';
import RegionsMapLite from '../maps/RegionsMapLite';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import { getHomebaseIslandKey } from '../../utils/homebaseGeography';

const BookedSelectScenario = ({ data, interviewId }) => {
  const navigate = useNavigate();
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [selectedArea, setSelectedArea] = useState(null);
  const [regionAreas, setRegionAreas] = useState([]);
  const [isUpdating, setIsUpdating] = useState(false);
  
  // Get master list of all areas and regions for validation
  const { regionsData: allRegions, areasData: allAreas } = useRegionsData();
  const islandKey = getHomebaseIslandKey(data);


  // Fetch areas for the selected region
  useEffect(() => {
    const fetchRegionAreas = async () => {
      if (!selectedRegion) {
        setRegionAreas([]);
        return;
      }
      
      try {
        // Find the region data to get its ID
        const regionData = allRegions?.find(r => r.title === selectedRegion);
        
        if (regionData) {
          // Fetch all areas in this region
          const areasQuery = `*[_type == "area" && ${islandScopedDocumentFilter()} && region._ref == $regionId]{
            _id,
            title,
            oneLiner,
            heroImage{asset->{url}, alt},
            slug,
            region->{
              _id,
              title,
              slug
            }
          } | order(title asc)`;
          
          const areas = await sanityFetch(areasQuery, { regionId: regionData._id });
          setRegionAreas(areas);
        }
      } catch (error) {
        console.error('[BookedSelectScenario] Error fetching region areas:', error);
      }
    };

    if (allRegions) {
      fetchRegionAreas();
    }
  }, [selectedRegion, allRegions]);

  // Early return check after hooks
  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-white/70">No homebase data available for this scenario.</p>
      </div>
    );
  }

  const handleEditInterview = () => {
    navigate(`/interview/review/${interviewId}`);
  };

  const handleConfirmHomebase = async () => {
    if (!selectedArea || !selectedRegion) return;
    
    setIsUpdating(true);
    
    try {
      // Find the selected area data
      const selectedAreaData = regionAreas.find(a => a.slug.current === selectedArea);
      if (!selectedAreaData) {
        throw new Error('Selected area not found');
      }
      
      // Update the interview in Firestore
      const docRef = doc(db, 'interviews', interviewId);
      await updateDoc(docRef, {
        island: islandKey,
        'lodging.bookedDetails.area': selectedAreaData.title,
        'lodging.bookedDetails.region': selectedRegion,
        selectedArea: selectedAreaData.title,
        selectedRegion,
        selectedAreaSlug: selectedAreaData.slug.current,
        selectedRegionSlug: selectedAreaData.region?.slug?.current || null,
        'metadata.lastUpdated': serverTimestamp(),
        'metadata.homebaseAreaConfirmed': true
      });
      
      // Navigate to homebase with refresh flag to trigger Scenario 2
      navigate(`/homebase/${interviewId}?refresh=true`);
    } catch (error) {
      console.error('[BookedSelectScenario] Error updating interview:', error);
      alert('Failed to save your location. Please try again.');
      setIsUpdating(false);
    }
  };

  const handleRegionSelect = (regionName) => {
    setSelectedRegion(regionName);
    // Clear area selection when region changes
    setSelectedArea(null);
    
    // Auto-select the only area when a region currently has a single child area.
    if (allAreas) {
      const matchingAreas = allAreas.filter((area) => area.region?.title === regionName);
      if (matchingAreas.length === 1) {
        setTimeout(() => setSelectedArea(matchingAreas[0].slug.current), 100);
      }
    }
  };

  const handleAreaSelect = (areaSlug) => {
    setSelectedArea(areaSlug);
  };

  const handleMapClick = (type, name) => {
    if (type === 'region' && allRegions) {
      // Validate region against master list
      const region = allRegions.find(r => 
        r.title === name ||
        r.title.toLowerCase() === name.toLowerCase()
      );
      
      if (region) {
        handleRegionSelect(region.title);
      }
    } else if (type === 'area' && allAreas) {
      // Validate area against master list
      const area = allAreas.find(a => 
        a.title === name ||
        a.title.toLowerCase() === name.toLowerCase() ||
        a.slug.current === name ||
        a.slug.current === name.toLowerCase()
      );
      
      if (area) {
        // If no region selected yet, auto-select the area's region
        if (!selectedRegion && area.region?.title) {
          setSelectedRegion(area.region.title);
        }
        
        setSelectedArea(area.slug.current);
      }
    }
  };

  const getCurrentList = () => {
    if (!selectedRegion) {
      // Show regions
      return {
        type: 'regions',
        items: allRegions?.map(region => ({
          key: region.title,
          name: region.title,
          description: region.oneLiner || region.shortDescription
        })) || []
      };
    } else {
      // Show areas in selected region
      return {
        type: 'areas',
        items: regionAreas.map(area => ({
          key: area.slug.current,
          name: area.title,
          description: area.oneLiner
        }))
      };
    }
  };

  const { type, items } = getCurrentList();

  return (
    <div className="min-h-screen">
      <div className="max-w-6xl mx-auto px-4 pb-8">
        
        {/* Primary CTAs - Primary hidden until both region and area selection */}
        <div className="flex flex-col md:flex-row md:justify-between gap-4 mb-4">
          <button 
            onClick={handleEditInterview}
            className="btn-secondary w-full md:w-72 px-8 py-4 text-lg"
          >
            Edit Interview
          </button>
          {selectedRegion && selectedArea && (
            <button 
              onClick={handleConfirmHomebase}
              className="btn-primary-cta w-full md:w-72 px-8 py-4 text-lg"
              disabled={isUpdating}
            >
              {isUpdating 
                ? 'Saving...'
                : `Confirm ${regionAreas.find(a => a.slug.current === selectedArea)?.title || 'Area'} as Homebase`
              }
            </button>
          )}
        </div>

        {/* Header Message */}
        <div className="mb-6">
          <h2 className="text-xl font-semibold mb-2 text-white">
            Tell us where you are booked so that we can anchor your trip in the right location
          </h2>
          <p className="text-white/90">
            {!selectedRegion 
              ? 'First, select the region where you are staying.'
              : !selectedArea
              ? `Now select the specific area within ${selectedRegion}.`
              : `Perfect! You've selected ${regionAreas.find(a => a.slug.current === selectedArea)?.title} in ${selectedRegion}.`
            }
          </p>
        </div>

        {/* Map + List Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-6 mb-8">
          {/* Map Column */}
          <div className="relative">
            <div className="h-[400px] lg:h-[500px] rounded-lg overflow-hidden border border-gray-600">
              {/* Only render map when we have master data loaded */}
              {allRegions && allAreas ? (
                <RegionsMapLite
                  islandKey={islandKey}
                  selectable={true}
                  areasSelectable={true} // Always allow area selection (we'll handle the logic in our handler)
                  selectedRegion={selectedRegion}
                  selectedArea={selectedArea ? regionAreas.find(a => a.slug.current === selectedArea)?.title : null}
                  onSelect={handleMapClick}
                  focusedRegionMode={!!selectedRegion} // Zoom to region when selected
                  className="w-full h-full"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gray-800 text-white">
                  <p>Loading map...</p>
                </div>
              )}
            </div>
            
            {/* Reset button - only show after region selection */}
            {selectedRegion && (
              <button
                onClick={() => {
                  setSelectedRegion(null);
                  setSelectedArea(null);
                }}
                className="absolute top-4 right-4 bg-gray-800/80 backdrop-blur-sm text-white px-3 py-2 rounded-lg text-sm hover:bg-gray-800/95 transition-colors border border-white/20"
              >
                Reset
              </button>
            )}
          </div>

          {/* List Column */}
          <div className="lg:w-[380px]">
            {/* Section Title */}
            <div className="mb-3">
              <h3 className="text-lg font-semibold text-white">
                {type === 'regions' ? 'Select Region' : `Areas in ${selectedRegion}`}
              </h3>
            </div>
            
            <div className="space-y-2">
              {items.map((item) => (
                <button
                  key={item.key}
                  className={`w-full text-left bg-white/5 border rounded-lg p-3 transition-all duration-200 cursor-pointer group ${
                    (type === 'regions' && selectedRegion === item.name) ||
                    (type === 'areas' && selectedArea === item.key)
                      ? 'border-green-400 bg-green-400/10'
                      : 'border-gray-600 hover:border-green-400 hover:bg-white/10'
                  }`}
                  onClick={() => {
                    if (type === 'regions') {
                      handleRegionSelect(item.name);
                    } else {
                      handleAreaSelect(item.key);
                    }
                  }}
                >
                  <div className="flex items-center justify-between mb-1">
                    <h4 className={`font-semibold transition-colors ${
                      (type === 'regions' && selectedRegion === item.name) ||
                      (type === 'areas' && selectedArea === item.key)
                        ? 'text-white'
                        : 'text-white group-hover:text-green-300'
                    }`}>
                      {item.name}
                    </h4>
                    {((type === 'regions' && selectedRegion === item.name) ||
                      (type === 'areas' && selectedArea === item.key)) && (
                      <span className="text-sm text-green-300">✓</span>
                    )}
                  </div>
                  <p className={`text-sm mt-0 transition-colors ${
                    (type === 'regions' && selectedRegion === item.name) ||
                    (type === 'areas' && selectedArea === item.key)
                      ? 'text-white/90'
                      : 'text-gray-300 group-hover:text-green-200'
                  }`}>
                    {item.description}
                  </p>
                </button>
              ))}
            </div>
            
            {/* Back button when showing areas */}
            {selectedRegion && (
              <div className="mt-4">
                <button
                  onClick={() => {
                    setSelectedRegion(null);
                    setSelectedArea(null);
                  }}
                  className="text-sm text-white/70 hover:text-white transition-colors"
                >
                  ← Back to Region Selection
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Bottom CTAs */}
        <div className="flex flex-col md:flex-row md:justify-between gap-4">
          <button 
            onClick={handleEditInterview}
            className="btn-secondary w-full md:w-72 px-8 py-4 text-lg"
          >
            Edit Interview
          </button>
          {selectedRegion && selectedArea && (
            <button 
              onClick={handleConfirmHomebase}
              className="btn-primary-cta w-full md:w-72 px-8 py-4 text-lg"
              disabled={isUpdating}
            >
              {isUpdating 
                ? 'Saving...'
                : `Confirm ${regionAreas.find(a => a.slug.current === selectedArea)?.title || 'Area'} as Homebase`
              }
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default BookedSelectScenario;
